import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { Clock, TrendingUp, AlertTriangle, CheckCircle, RefreshCw, ArrowRight, Award, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const TATReport = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('pr_to_rfq');

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/reports/tat');
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching TAT report:', error);
      toast.error('Failed to load TAT report');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      on_time: 'bg-green-100 text-green-800',
      delayed: 'bg-yellow-100 text-yellow-800',
      critical: 'bg-red-100 text-red-800'
    };
    const labels = {
      on_time: 'On Time',
      delayed: 'Delayed',
      critical: 'Critical'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.delayed}`}>
        {labels[status] || status}
      </span>
    );
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${color} rounded-lg`}>
          <Icon size={20} />
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-900">{value}</div>
          <div className="text-xs text-slate-500">{title}</div>
          {subtitle && <div className="text-xs text-slate-400">{subtitle}</div>}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  return (
    <div data-testid="tat-report-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">TAT Report</h1>
          <p className="text-sm text-slate-500 mt-1">Turn Around Time Analysis - Identify delays in procurement process</p>
        </div>
        <Button onClick={fetchReport} variant="outline" disabled={loading}>
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b mb-6 rounded-t-lg">
        <div className="flex">
          <button
            onClick={() => setActiveTab('pr_to_rfq')}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'pr_to_rfq'
                ? 'border-blue-600 text-blue-600 bg-blue-50'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <FileText size={18} />
            PR → RFQ Conversion
          </button>
          <button
            onClick={() => setActiveTab('rfq_to_award')}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'rfq_to_award'
                ? 'border-purple-600 text-purple-600 bg-purple-50'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Award size={18} />
            RFQ → Award
          </button>
        </div>
      </div>

      {/* PR to RFQ Tab */}
      {activeTab === 'pr_to_rfq' && report?.pr_to_rfq && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard
              title="Total Converted"
              value={report.pr_to_rfq.stats.count}
              icon={CheckCircle}
              color="bg-blue-100 text-blue-600"
            />
            <StatCard
              title="Average TAT"
              value={`${report.pr_to_rfq.stats.avg} days`}
              icon={Clock}
              color="bg-indigo-100 text-indigo-600"
            />
            <StatCard
              title="Fastest"
              value={`${report.pr_to_rfq.stats.min} days`}
              icon={TrendingUp}
              color="bg-green-100 text-green-600"
            />
            <StatCard
              title="Slowest"
              value={`${report.pr_to_rfq.stats.max} days`}
              icon={AlertTriangle}
              color="bg-red-100 text-red-600"
            />
            <StatCard
              title="On-Time Rate"
              value={`${report.pr_to_rfq.stats.on_time_pct}%`}
              subtitle="≤ 2 days"
              icon={CheckCircle}
              color="bg-emerald-100 text-emerald-600"
            />
          </div>

          {/* Benchmark Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Benchmarks for PR → RFQ Conversion</h3>
            <div className="flex gap-6 text-sm">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                On Time: ≤ 2 days
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                Delayed: 2-5 days
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                Critical: &gt; 5 days
              </span>
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Conversion Details (Slowest First)</h2>
              <p className="text-sm text-slate-500">Showing top 50 records sorted by TAT</p>
            </div>
            {report.pr_to_rfq.data.length > 0 ? (
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">PR Number</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Requester</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">PR Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Converted</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">TAT (Days)</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {report.pr_to_rfq.data.map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-sm font-medium text-slate-900">{item.pr_number}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.requester_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.pr_created_at}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.converted_at}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-mono font-bold ${
                          item.tat_days <= 2 ? 'text-green-600' :
                          item.tat_days <= 5 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {item.tat_days}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(item.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-slate-500">
                No PR to RFQ conversion data available
              </div>
            )}
          </div>
        </div>
      )}

      {/* RFQ to Award Tab */}
      {activeTab === 'rfq_to_award' && report?.rfq_to_award && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard
              title="Total Awarded"
              value={report.rfq_to_award.stats.count}
              icon={Award}
              color="bg-purple-100 text-purple-600"
            />
            <StatCard
              title="Average TAT"
              value={`${report.rfq_to_award.stats.avg} days`}
              icon={Clock}
              color="bg-indigo-100 text-indigo-600"
            />
            <StatCard
              title="Fastest"
              value={`${report.rfq_to_award.stats.min} days`}
              icon={TrendingUp}
              color="bg-green-100 text-green-600"
            />
            <StatCard
              title="Slowest"
              value={`${report.rfq_to_award.stats.max} days`}
              icon={AlertTriangle}
              color="bg-red-100 text-red-600"
            />
            <StatCard
              title="On-Time Rate"
              value={`${report.rfq_to_award.stats.on_time_pct}%`}
              subtitle="≤ 7 days"
              icon={CheckCircle}
              color="bg-emerald-100 text-emerald-600"
            />
          </div>

          {/* Benchmark Info */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-purple-900 mb-2">Benchmarks for RFQ → Award</h3>
            <div className="flex gap-6 text-sm">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                On Time: ≤ 7 days
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                Delayed: 7-14 days
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                Critical: &gt; 14 days
              </span>
            </div>
          </div>

          {/* Data Table */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Award Details (Slowest First)</h2>
              <p className="text-sm text-slate-500">Showing top 50 records sorted by TAT</p>
            </div>
            {report.rfq_to_award.data.length > 0 ? (
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">RFQ Number</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Awarded To</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">RFQ Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Awarded</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">TAT (Days)</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {report.rfq_to_award.data.map((item, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-sm font-medium text-slate-900">{item.rfq_number}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.awarded_to}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.rfq_created_at}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{item.awarded_at}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-mono font-bold ${
                          item.tat_days <= 7 ? 'text-green-600' :
                          item.tat_days <= 14 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {item.tat_days}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(item.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-8 text-center text-slate-500">
                No RFQ to Award data available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TATReport;
